import { useEffect, useRef, useState } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { isAbortError } from "@/lib/abort-error";
import { restartEnvironment } from "@/lib/api";
import { waitForEnvironmentRestart } from "@/lib/environment-lifecycle";
import { showEnvironmentStopNotice } from "@/lib/environment-stop-notice";
import { formatDateTime } from "@/lib/format-datetime";
import { useHasPermission } from "@/lib/permissions";
import { useChatStore } from "@/stores/useChatStore";
import { useToastStore } from "@/stores/useToastStore";

/**
 * サーバーの保守で現在チャットのエージェントが再起動される予定を知らせる。使われているかは外から確実に分からない
 * ため自動では移さず、期限より前に都合のよいときに再起動するか、期限まで待つかをユーザーが選ぶ。
 * 再起動したエージェントは新しいサーバーで起動するため、予定は消える。
 */
export function ScheduledRestartBanner() {
	const environmentId = useChatStore((s) => s.environmentId);
	const scheduledRestartAt = useChatStore((s) => s.environmentScheduledRestartAt);
	const canManageAgents = useHasPermission("manageAgents");
	const [confirming, setConfirming] = useState(false);
	const [restarting, setRestarting] = useState(false);
	const restartControllerRef = useRef<AbortController | null>(null);
	useEffect(() => {
		const controllerRef = restartControllerRef;
		return () => controllerRef.current?.abort();
	}, []);

	if (environmentId === null || scheduledRestartAt === null) return null;

	// 失敗は ConfirmDialog がその場に出すため、再起動の要求はそのまま投げる。
	const handleRestart = async () => {
		if (restartControllerRef.current) return;
		const controller = new AbortController();
		restartControllerRef.current = controller;
		setRestarting(true);
		try {
			const result = await restartEnvironment(environmentId, controller.signal);
			setConfirming(false);
			showEnvironmentStopNotice(result);
			const { setEnvironmentStatus } = useChatStore.getState();
			setEnvironmentStatus(environmentId, "shuttingDown", null);
			await waitForEnvironmentRestart(environmentId, {
				signal: controller.signal,
				onStatusChanged: (entry) =>
					setEnvironmentStatus(environmentId, entry.status, entry.scheduledRestartAt ?? null),
			}).catch((err: unknown) => {
				if (isAbortError(err)) return;
				console.error("ScheduledRestartBanner restart polling failed", err);
				useToastStore.getState().show({
					type: "error",
					message:
						err instanceof Error ? err.message : "エージェントの再起動を確認できませんでした。",
				});
			});
		} finally {
			restartControllerRef.current = null;
			if (!controller.signal.aborted) setRestarting(false);
		}
	};

	return (
		<div
			data-testid="scheduled-restart-banner"
			className="text-caption"
			style={{
				display: "flex",
				alignItems: "center",
				gap: 12,
				margin: "0 0 8px",
				padding: "8px 12px",
				background: "var(--warning-subtle)",
				color: "var(--warning)",
				borderRadius: "var(--radius-4)",
			}}
		>
			<p className="m-0" style={{ flex: 1 }}>
				サーバーの保守のため、{formatDateTime(scheduledRestartAt)}
				にこのエージェントを再起動します。
				{canManageAgents && "それより前に、都合のよいときに再起動することもできます。"}
				ファイルは残りますが、実行中の処理は止まります。学習など長く続く処理は、途中から再開できるようチェックポイントをこまめに保存してください。
			</p>
			{canManageAgents && (
				<ConfirmButton
					label="今すぐ再起動"
					loading={restarting}
					variant="secondary"
					size="sm"
					data-testid="scheduled-restart-now"
					onClick={() => setConfirming(true)}
				/>
			)}
			<ConfirmDialog
				open={confirming}
				title="エージェントを再起動しますか？"
				message="実行中のチャットや処理は中断されます。エージェント環境内のファイルは残ります。"
				confirmLabel="再起動"
				onConfirm={handleRestart}
				onCancel={() => setConfirming(false)}
			/>
		</div>
	);
}
